import React, { useState, memo, useRef, useEffect } from "react";
import {
  AutoResizingTextArea,
  Lock,
  Unlock,
  Container,
  MenuToggle,
  CustomNoteMenu,
  FillColor,
  TextColor,
  TextSize,
  ConfirmButton,
  DeleteButton,
  MoveButton,
  RestoreButton,
  SizeInputContainer,
} from "./style";
import { useDispatch } from "react-redux";
import { useStoreState } from "react-flow-renderer";
import {
  handleUpdateCustomNote,
  handleRemoveCustomNote,
} from "../../../state/activedFlows/actions";
import { activateOverlayForMove } from "../../../state/activedFlows/actions";
import ColorPalet from "./ColorPalet";

const CustomNote = memo(({ data, id }) => {
  const initialState = {
    text: data.text || "",
    color: data.color,
    textColor: data.textColor,
    textSize: data.textSize,
    width: data.width,
    height: data.height,
    lock: data.lock || false,
    isEditable: false,
  };

  const [note, setNote] = useState(initialState);
  const initialNoteRef = useRef(initialState);

  const dispatch = useDispatch();

  const handleNoteChange = (key, value) => {
    setNote((prevNote) => ({ ...prevNote, [key]: value }));
  };

  const flowId = useStoreState(
    (state) => state.nodes.find((item) => item.flowId)?.flowId
  );

  const handleSaveChanges = () => {
    const newWidth = containerAreaRef.current.offsetWidth;
    handleNoteChange("width", newWidth);
    dispatch(handleUpdateCustomNote({ noteId: id, newData: { ...note } }));
    initialNoteRef.current = { ...note };
    handleClose();
  };

  const handleCancelChanges = () => {
    setNote(initialNoteRef.current);
    handleClose();
  };
  //? Small Menu Block
  const [showTextSizeSelector, setShowTextSizeSelector] = useState(false);
  const fillColorRef = useRef(null);
  const textColorRef = useRef(null);
  const [collorPopper, setCollorPopper] = useState(false);
  const [type, setType] = useState("");

  const handleCloseCP = () => {
    setCollorPopper(false);
  };

  const handleRemoveCN = () => {
    if (window.confirm("Tem certeza de que deseja remover esta nota?")) {
      dispatch(
        handleRemoveCustomNote({
          noteId: id,
        })
      );
    }
  };

  const clearTextSelection = () => {
    if (window.getSelection) {
      window.getSelection().removeAllRanges();
    } else if (document.selection) {
      // For IE
      document.selection.empty();
    }
  };

  const handleIncreaseTextSize = () => {
    handleNoteChange("textSize", parseInt(note.textSize, 10) + 1);
  };

  const textInputRef = useRef(null);

  const handleTextSizeChange = (newSize) => {
    handleNoteChange("textSize", newSize);
    setTimeout(() => {
      textInputRef.current?.focus();
    }, 0);
  };

  const TextSizeSelector = () => (
    <SizeInputContainer show={showTextSizeSelector}>
      <button
        onClick={() =>
          handleNoteChange("textSize", Math.max(1, note.textSize - 1))
        }
        style={{ color: "#a00" }}
      >
        -
      </button>
      <input
        ref={textInputRef}
        type="number"
        value={note.textSize}
        onChange={(e) => handleTextSizeChange(e.target.value)}
      />
      <button onClick={handleIncreaseTextSize} style={{ color: "#0a0" }}>
        +
      </button>
    </SizeInputContainer>
  );
  // End of Block

  const handleLock = () => handleNoteChange("lock", !note.lock);
  const handleMenuToggle = () =>
    handleNoteChange("isEditable", !note.isEditable);

  const handleNoteColor = (newColor) => handleNoteChange("color", newColor);
  const handleTextColor = (newColor) => handleNoteChange("textColor", newColor);
  const handleTextSize = (newSize) => handleNoteChange("textSize", newSize);
  const handleClose = () => {
    handleNoteChange("isEditable", false);
    clearTextSelection();
    setShowTextSizeSelector(false);
  };

  const getNoteStyle = () => {
    return {
      width: note.width,
      height: note.height,
      backgroundColor: note.color,
    };
  };

  const handleMoveNote = () => {
    const noteStyle = getNoteStyle();
    const noteId = id;
    dispatch(activateOverlayForMove(noteId, flowId, noteStyle));
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (collorPopper) {
        return;
      }
      if (!containerAreaRef.current.contains(event.target)) {
        handleClose();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [collorPopper]);

  const textAreaRef = useRef(null);
  const containerAreaRef = useRef(null);

  const adjustHeight = () => {
    if (textAreaRef.current) {
      textAreaRef.current.style.maxHeight = "1px";

      const newHeight = textAreaRef.current.scrollHeight;
      textAreaRef.current.style.height = `${newHeight}px`;
      textAreaRef.current.style.maxHeight = `${newHeight}px`;
      handleNoteChange("height", newHeight);
      if (containerAreaRef.current) {
        containerAreaRef.current.style.height = `${newHeight}px`;
      }
    }
  };
  useEffect(() => {
    if (!note.isEditable) {
      const newWidth = containerAreaRef.current.offsetWidth;
      handleNoteChange("width", newWidth);
      const initialNote = initialNoteRef.current;
      const hasChanges =
        note.text !== initialNote.text ||
        note.color !== initialNote.color ||
        note.textColor !== initialNote.textColor ||
        note.textSize !== initialNote.textSize ||
        note.width !== initialNote.width;

      if (hasChanges) {
        if (
          window.confirm(
            "Você tem alterações não salvas. Deseja salvar antes de fechar?"
          )
        ) {
          handleSaveChanges();
        } else {
          handleCancelChanges();
        }
      }
    }
  }, [note.isEditable]);

  useEffect(() => {
    adjustHeight();
  }, [note.text, note.isEditable]);

  return (
    <Container
      onDoubleClick={() => {
        if (note.lock) {
          handleNoteChange("isEditable", true);
        }
      }}
      isLocked={note.lock}
      textColor={note.textColor}
      noteColor={note.color}
      width={note.width}
      height={note.height}
      ref={containerAreaRef}
      resizable={note.isEditable}
    >
      <AutoResizingTextArea
        value={note.text}
        onClick={(e) => {
          if (!note.isEditable) {
            e.preventDefault();
          }
        }}
        onChange={(e) => handleNoteChange("text", e.target.value)}
        ref={textAreaRef}
        width={note.width}
        height={note.height}
        placeholder="Sua Nota"
        textColor={note.textColor}
        noteColor={note.color}
        textSize={note.textSize}
        readOnly={!note.isEditable || !note.lock}
      />
      {note.lock ? (
        <Unlock onClick={handleLock} />
      ) : (
        <Lock onClick={handleLock} />
      )}
      <MenuToggle
        onClick={note.lock ? handleMenuToggle : null}
        isOpen={note.isEditable}
        isLocked={note.lock}
      />
      {note.isEditable && (
        <CustomNoteMenu isExtended={showTextSizeSelector}>
          <FillColor
            ref={fillColorRef}
            onClick={(e) => {
              e.stopPropagation();
              setType("NoteColor");
              setCollorPopper(true);
            }}
          />

          <TextColor
            ref={textColorRef}
            onClick={(e) => {
              e.stopPropagation();
              setType("TextColor");
              setCollorPopper(true);
            }}
          />
          <TextSize onClick={() => setShowTextSizeSelector((show) => !show)} />
          <TextSizeSelector />
          <MoveButton onClick={handleMoveNote} />
          <ConfirmButton
            onClick={() => {
              handleSaveChanges();
            }}
          />
          <RestoreButton
            onClick={() => {
              handleCancelChanges();
            }}
          />
          <DeleteButton onClick={() => handleRemoveCN()} />
        </CustomNoteMenu>
      )}
      <ColorPalet
        anchorRef={type === "NoteColor" ? fillColorRef : textColorRef}
        isOpen={collorPopper}
        onClose={() => handleCloseCP}
        handleNoteColor={handleNoteColor}
        type={type}
        handleTextColor={handleTextColor}
      />
    </Container>
  );
});

export default CustomNote;
